import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useCurrentUser } from '../../../shared/features/session/components/CurrentUserProvider';
import { useToast } from '../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../shared/policies/api-error-message';
import { ApiError } from '../../../shared/shared.api';
import { blockersFor, initialReadings, receivedToast, toReceiveInput, type Readings } from '../policies/receive-readings.policy';
import { YARD_PATH } from '../yard.paths';
import { getYardReceipt, receiveTruck, type YardReceiptView } from '../yard.api';

export type ReceiptState = { status: 'loading' } | { status: 'loaded'; view: YardReceiptView } | { status: 'forbidden' } | { status: 'error'; error: unknown };

const toErrorState = (error: unknown): ReceiptState =>
  error instanceof ApiError && error.status === 403 ? { status: 'forbidden' } : { status: 'error', error };

/** One trip's receive screen: the receipt as the server reads it, the two readings as typed, and the Receive action. */
export function useReceiveTruck(tripId: string) {
  const toast = useToast();
  const navigate = useNavigate();
  const { state: user } = useCurrentUser();
  const [state, setState] = useState<ReceiptState>({ status: 'loading' });
  const [readings, setReadings] = useState<Readings>({ odometerKm: null, fuelLevelPercent: null });
  const [isReceiving, setIsReceiving] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getYardReceipt(tripId)
      .then((view) => {
        if (cancelled) return;
        setReadings(initialReadings(view));
        setState({ status: 'loaded', view });
      })
      .catch((error) => {
        if (!cancelled) setState(toErrorState(error));
      });
    return () => {
      cancelled = true;
    };
  }, [tripId]);

  const permissions = user.status === 'authenticated' ? user.user.permissions : [];
  const view = state.status === 'loaded' ? state.view : null;

  /** After a refused receipt: the trip is not as the screen showed it, so show it as it is now, keeping the readings the supervisor typed. */
  async function reload(): Promise<void> {
    try {
      setState({ status: 'loaded', view: await getYardReceipt(tripId) });
    } catch (error) {
      setState(toErrorState(error));
    }
  }

  async function receive(): Promise<void> {
    setIsReceiving(true);
    setRefusal(null);
    try {
      const result = await receiveTruck(tripId, toReceiveInput(readings));
      toast.success(receivedToast(result.truckRegistration, result.workOrders.length));
      navigate(YARD_PATH);
    } catch (error) {
      setRefusal(apiErrorMessage(error) ?? 'Could not receive this truck. Try again.');
      if (error instanceof ApiError && error.status === 409) await reload();
    } finally {
      setIsReceiving(false);
    }
  }

  return {
    state,
    view,
    readings,
    setReadings,
    blockers: view ? blockersFor(view, readings) : [],
    canReceive: permissions.includes('truck.receive'),
    isReceiving,
    refusal,
    receive,
  };
}
